import { useMemo, useRef, useState } from "react";
import { Input, Select } from "antd";
import { get, isEmpty, isNil } from "lodash";
import PropTypes from "prop-types";
import {
  CHECK_KINDS,
  checkKindOf,
  isMustBeChecked,
  newRule,
  ruleMessage,
  RULES_BY_KIND,
  shownRules,
  withMustBeChecked,
} from "./answer-checks";
import { useCanvasSelection } from "./canvas-selection";
import { checkedFieldsOf } from "./checked-fields";
import { looksLikeNeed } from "./needs-fields";
import { looksLikeNeedPath, useRecommendation } from "./recommendation-guide";
import { ruleTitle, SLOT_PROBLEMS } from "./screen-fields";
import {
  buttonChoices,
  buttonTextOf,
  CONTENT_MODES,
  contentModeOf,
  fieldOptions,
  fitsSlot,
  registryFieldOf,
  searchable,
} from "./slot-editing";
import WorkshopFieldCreate, {
  CUSTOM_ENTITY,
  customKeyOf,
  fieldDraftFor,
  fieldDraftOf,
  isCustomPath,
  keyFromLabel,
} from "./workshop-field-create";
import WorkshopCheckedCard from "./WorkshopCheckedCard";
import WorkshopNeedsCard from "./WorkshopNeedsCard";
import { actionOption, choiceOf } from "./WorkshopSlotEditors";
import {
  BINDING_KINDS,
  fieldPathOf,
  fillTemplate,
  isFileSlot,
  SLOT_TYPES,
} from "./workshop-view";

const NO_RULE = "none";

const FIELD_FORMS = { CREATE: "create", EDIT: "edit" };

const Segmented = ({ label, options, value, disabled, onChange }) => (
  <div role="group" aria-label={label} className="workshop-segmented">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={value === option.value}
        disabled={disabled}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

Segmented.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.any, label: PropTypes.string }),
  ).isRequired,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

Segmented.defaultProps = {
  value: null,
  disabled: false,
};

const Question = ({ label, children }) => (
  <div className="workshop-quick__question">
    <span className="workshop-quick__label">{label}</span>
    {children}
  </div>
);

Question.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const requiredOf = (check, kind) =>
  kind === CHECK_KINDS.CHECKBOX
    ? isMustBeChecked(check)
    : Boolean(get(check, "required"));

const ruleOf = (check) => get(shownRules(check), [0, "validator"]) || NO_RULE;

const withAnswer = ({ check, kind, required, rule, checkLabels }) => {
  const base =
    kind === CHECK_KINDS.CHECKBOX
      ? withMustBeChecked(check, required)
      : { ...check, required };
  if (rule === ruleOf(check)) return base;
  const shown = shownRules(base);
  const kept = (base.rules || []).filter((item) => !shown.includes(item));
  const created = rule === NO_RULE ? null : newRule(rule);
  const added = isNil(created)
    ? []
    : [{ ...created, message: ruleMessage(created, checkLabels) }];
  return { ...base, rules: [...kept, ...added, ...shown.slice(1)] };
};

const WorkshopQuickEdit = ({
  row,
  labels,
  fieldLabels,
  checkLabels,
  registry,
  known,
  actions,
  switches,
  variables,
  checkFor,
  saving,
  onEdit,
  onCreateField,
  onEditField,
  onUpload,
  onSaveCheck,
  onDone,
  onCancel,
}) => {
  const { slot } = row;
  const fileRef = useRef(null);
  const { needsFields, usage, stepNames } = useRecommendation();
  const { stepId } = useCanvasSelection();
  const sourceValue = get(slot, "binding.sourceValue") || "";
  const dataSource =
    get(slot, "binding.kind") === BINDING_KINDS.DATA
      ? get(slot, "binding.source") || null
      : null;
  const savedPath = fieldPathOf(slot);
  const fileSlot = isFileSlot(slot);
  const customAllowed = !fileSlot && isNil(get(slot, "collection"));
  const button =
    slot.type === SLOT_TYPES.ACTION
      ? buttonChoices({ slot, actions, switches, labels: fieldLabels, known })
      : null;

  const [mode, setMode] = useState(contentModeOf(slot));
  const [text, setText] = useState(sourceValue);
  const [dataField, setDataField] = useState(dataSource);
  const [imageUrl, setImageUrl] = useState(get(slot, "src") || "");
  const [savedIn, setSavedIn] = useState(savedPath);
  const variableOf = (path) =>
    (variables || []).find((variable) => variable.field === path) || null;
  const variable = variableOf(savedIn);
  const kind = checkKindOf(slot, variable);
  const check = isNil(variable) ? null : checkFor(variable);
  const [required, setRequired] = useState(requiredOf(check, kind));
  const [rule, setRule] = useState(ruleOf(check));
  const missingField = row.problem === SLOT_PROBLEMS.TO_CREATE;
  const [fieldForm, setFieldForm] = useState(
    missingField && isCustomPath(savedPath) && customAllowed
      ? FIELD_FORMS.CREATE
      : null,
  );
  const [action, setAction] = useState(get(button, "selected") || undefined);
  const [buttonText, setButtonText] = useState(buttonTextOf(slot));

  const allFields = useMemo(() => fieldOptions(registry), [registry]);
  const checkedFields = useMemo(() => checkedFieldsOf(variables), [variables]);
  const writableFields = useMemo(
    () =>
      fieldOptions(registry, {
        writableOnly: true,
        attachments: fileSlot,
        keep: savedPath,
        needsGroup: (
          <span className="workshop-needs-group">
            <span>{labels.NEEDS_GROUP}</span>
            <span className="workshop-needs-group__hint">
              {labels.NEEDS_GROUP_HINT}
            </span>
          </span>
        ),
        needsHint: (path) => {
          const steps = get(usage, path) || [];
          return steps.length
            ? fillTemplate(labels.NEEDS_USED, {
                steps: steps.map((step) => step.name).join(", "),
              })
            : labels.NEEDS_UNUSED;
        },
      }),
    [
      registry,
      fileSlot,
      savedPath,
      usage,
      labels.NEEDS_GROUP,
      labels.NEEDS_GROUP_HINT,
      labels.NEEDS_USED,
      labels.NEEDS_UNUSED,
    ],
  );

  const pickSavedIn = (path) => {
    setSavedIn(path);
    setFieldForm(null);
    const next = variableOf(path);
    const nextCheck = isNil(next) ? null : checkFor(next);
    const nextKind = checkKindOf(slot, next);
    setRequired(requiredOf(nextCheck, nextKind));
    setRule(ruleOf(nextCheck));
  };

  const textPatch = () => {
    if (mode === CONTENT_MODES.DATA)
      return dataField && dataField !== dataSource ? { field: dataField } : {};
    const trimmed = text.trim();
    return {
      ...(trimmed && trimmed !== sourceValue.trim() ? { text: trimmed } : {}),
      ...(isNil(dataSource) ? {} : { field: "" }),
    };
  };

  const imagePatch = () => {
    const url = imageUrl.trim();
    return url && url !== (get(slot, "src") || "") ? { imageUrl: url } : {};
  };

  const buttonPatch = () => {
    const trimmed = buttonText.trim();
    const actionChanged = (action || "") !== (get(button, "selected") || "");
    const chosenSwitch =
      (switches || []).find(
        (item) => item.value === action && !item.disabled,
      ) || null;
    return {
      ...(trimmed && trimmed !== buttonTextOf(slot).trim()
        ? { text: trimmed }
        : {}),
      ...choiceOf({
        actionChanged,
        chosenSwitch,
        draftAction: action,
        keptSetField: get(button, "keptSetField"),
      }),
    };
  };

  const inputPatch = () =>
    savedIn && savedIn !== savedPath ? { field: savedIn } : {};

  const patchOf = {
    [SLOT_TYPES.TEXT]: textPatch,
    [SLOT_TYPES.IMAGE]: imagePatch,
    [SLOT_TYPES.ACTION]: buttonPatch,
    [SLOT_TYPES.INPUT]: inputPatch,
  }[slot.type];
  const patch = patchOf ? patchOf() : {};
  const answerChanged =
    slot.type === SLOT_TYPES.INPUT &&
    !isNil(check) &&
    (required !== requiredOf(check, kind) || rule !== ruleOf(check));
  const changed = !isEmpty(patch) || answerChanged;

  const apply = async () => {
    if (!isEmpty(patch)) {
      const saved = await onEdit(row, patch);
      if (!saved) return;
    }
    if (answerChanged)
      onSaveCheck(withAnswer({ check, kind, required, rule, checkLabels }));
    onDone();
  };

  const renderText = () => (
    <Question label={labels.TEXT_ASK}>
      <Segmented
        label={labels.TEXT_ASK}
        value={mode}
        disabled={saving}
        options={[
          { value: CONTENT_MODES.FIXED, label: labels.FIXED },
          { value: CONTENT_MODES.DATA, label: labels.DATA },
        ]}
        onChange={setMode}
      />
      {mode === CONTENT_MODES.FIXED ? (
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 6 }}
          aria-label={labels.TEXT_LABEL}
          value={text}
          disabled={saving}
          onChange={(event) => setText(event.target.value)}
        />
      ) : (
        <Select
          showSearch
          virtual={false}
          listHeight={320}
          className="workshop-select"
          aria-label={labels.DATA_LABEL}
          placeholder={labels.PICK}
          options={allFields}
          filterOption={searchable}
          value={dataField || undefined}
          disabled={saving}
          onChange={setDataField}
        />
      )}
    </Question>
  );

  const renderImage = () => (
    <Question label={labels.IMAGE_ASK}>
      {slot.placeholder || !get(slot, "src") ? (
        <span className="workshop-quick__empty-image">{labels.NO_IMAGE}</span>
      ) : (
        <img
          className="workshop-quick__image"
          src={get(slot, "src")}
          alt={slot.label || ""}
        />
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files && event.target.files[0];
          event.target.value = "";
          if (file) onUpload(row, file);
        }}
      />
      <button
        type="button"
        className="workshop-quick__secondary"
        disabled={saving}
        onClick={() => fileRef.current && fileRef.current.click()}
      >
        {labels.PICK_FILE}
      </button>
      <span className="workshop-quick__label workshop-quick__label--small">
        {labels.IMAGE_URL}
      </span>
      <Input
        aria-label={labels.IMAGE_URL}
        placeholder="https://…"
        value={imageUrl}
        disabled={saving}
        onChange={(event) => setImageUrl(event.target.value)}
      />
    </Question>
  );

  const savedInLabel = fileSlot ? labels.FILE_SAVED_IN : labels.SAVED_IN;
  const savedField = registryFieldOf(registry, savedIn);
  const shownMissing = missingField && savedIn === savedPath;
  const editable = isCustomPath(savedIn) && !isNil(savedField);
  const needLike =
    looksLikeNeedPath(savedIn) ||
    (isCustomPath(savedIn) && looksLikeNeed(get(savedField, "label")));

  const linkField = async (path) => {
    pickSavedIn(path);
    const saved = await onEdit(row, { field: path });
    if (!saved) pickSavedIn(savedPath);
  };

  const createDraft = () => {
    const draft = fieldDraftFor(slot);
    return shownMissing
      ? draft
      : { ...draft, key: keyFromLabel(get(slot, "label")) || draft.key };
  };

  const createField = async (field) => {
    const failure = await onCreateField(row, field);
    if (failure) return failure;
    pickSavedIn(`${CUSTOM_ENTITY}.${field.key}`);
    return null;
  };

  const editField = async (field) => {
    const failure = await onEditField(row, field);
    if (failure) return failure;
    setFieldForm(null);
    return null;
  };

  const renderFieldForm = () =>
    fieldForm === FIELD_FORMS.EDIT ? (
      <WorkshopFieldCreate
        key={`${FIELD_FORMS.EDIT}:${savedIn}`}
        labels={{ ...fieldLabels.CREATE, ...fieldLabels.EDIT }}
        initial={fieldDraftOf(savedField, customKeyOf(savedIn))}
        editing
        scopeNote={
          get(savedField, "scoped")
            ? fieldLabels.EDIT.OWN
            : fieldLabels.EDIT.SHARED
        }
        disabled={saving}
        onSubmit={editField}
        onCancel={() => setFieldForm(null)}
      />
    ) : (
      <WorkshopFieldCreate
        key={FIELD_FORMS.CREATE}
        labels={fieldLabels.CREATE}
        initial={createDraft()}
        scopeNote={fieldLabels.CREATE.SCOPE}
        disabled={saving}
        onSubmit={createField}
        onCancel={() => setFieldForm(null)}
      />
    );

  const renderFieldLinks = () =>
    !customAllowed || !isNil(fieldForm) ? null : (
      <div className="workshop-quick__links">
        {editable ? (
          <button
            type="button"
            className="workshop-quick__link"
            disabled={saving}
            onClick={() => setFieldForm(FIELD_FORMS.EDIT)}
          >
            {labels.EDIT_FIELD}
          </button>
        ) : null}
        <button
          type="button"
          className="workshop-quick__link"
          disabled={saving}
          onClick={() => setFieldForm(FIELD_FORMS.CREATE)}
        >
          {labels.CREATE_FIELD}
        </button>
      </div>
    );

  const renderAnswer = () =>
    isNil(check) ? (
      <span className="workshop-quick__note">
        {fileSlot ? labels.FILE_PICK_FIRST : labels.PICK_SAVED_FIRST}
      </span>
    ) : (
      <>
        <Question label={labels.ANSWER}>
          <Segmented
            label={labels.ANSWER}
            value={required}
            disabled={saving}
            options={[
              {
                value: true,
                label:
                  kind === CHECK_KINDS.CHECKBOX
                    ? labels.MUST_BE_CHECKED
                    : labels.REQUIRED,
              },
              { value: false, label: labels.OPTIONAL },
            ]}
            onChange={setRequired}
          />
        </Question>
        {(RULES_BY_KIND[kind] || []).length ? (
          <Question label={labels.CHECK}>
            <Select
              virtual={false}
              className="workshop-select"
              aria-label={labels.CHECK}
              value={rule}
              disabled={saving}
              options={[
                { value: NO_RULE, label: labels.NO_CHECK },
                ...RULES_BY_KIND[kind].map((validator) => ({
                  value: validator,
                  label: ruleTitle({ validator }, checkLabels.RULES),
                })),
              ]}
              onChange={setRule}
            />
          </Question>
        ) : null}
      </>
    );

  const renderInput = () => (
    <>
      {fileSlot ? null : (
        <WorkshopNeedsCard
          slot={slot}
          savedIn={savedIn}
          savedPath={savedPath}
          needLike={needLike}
          needsFields={needsFields}
          stepName={get(stepNames, stepId) || ""}
          labels={labels.NEEDS}
          saving={saving}
          onUse={linkField}
          onScale={() => onEdit(row, { field: savedPath })}
        />
      )}
      {fileSlot ? null : (
        <WorkshopCheckedCard
          slot={slot}
          savedIn={savedIn}
          rule={rule}
          savedRule={ruleOf(check)}
          checkedFields={checkedFields}
          ruleLabel={(validator) => ruleTitle({ validator }, checkLabels.RULES)}
          labels={labels.CHECKED}
          saving={saving}
          onUse={linkField}
          onCheck={setRule}
        />
      )}
      <Question label={savedInLabel}>
        <Select
          showSearch
          virtual={false}
          listHeight={320}
          className="workshop-select"
          aria-label={savedInLabel}
          placeholder={labels.PICK}
          options={writableFields}
          optionRender={actionOption}
          filterOption={searchable}
          value={savedIn || undefined}
          disabled={saving}
          onChange={pickSavedIn}
        />
        {shownMissing ? (
          <span className="workshop-quick__note">
            {isCustomPath(savedIn) && customAllowed
              ? labels.FIELD_TO_CREATE
              : labels.FIELD_MISSING}
          </span>
        ) : null}
        {savedIn && !fitsSlot(registry, slot, savedIn) ? (
          <span className="workshop-quick__note">
            {fileSlot ? labels.FILE_WRONG_FIELD : labels.ANSWER_WRONG_FIELD}
          </span>
        ) : null}
        {renderFieldLinks()}
      </Question>
      {isNil(fieldForm) ? renderAnswer() : renderFieldForm()}
    </>
  );

  const renderButton = () => (
    <>
      <Question label={labels.BUTTON_ASK}>
        <Select
          showSearch
          virtual={false}
          listHeight={360}
          className="workshop-select"
          aria-label={labels.BUTTON_ASK}
          placeholder={labels.PICK}
          options={get(button, "options") || []}
          optionRender={actionOption}
          filterOption={searchable}
          value={action}
          disabled={saving}
          onChange={setAction}
        />
      </Question>
      <Question label={labels.BUTTON_TEXT}>
        <Input
          aria-label={labels.BUTTON_TEXT}
          value={buttonText}
          disabled={saving}
          onChange={(event) => setButtonText(event.target.value)}
        />
      </Question>
    </>
  );

  const body = {
    [SLOT_TYPES.TEXT]: renderText,
    [SLOT_TYPES.IMAGE]: renderImage,
    [SLOT_TYPES.INPUT]: renderInput,
    [SLOT_TYPES.ACTION]: renderButton,
  }[slot.type];

  return (
    <div className="workshop-quick">
      {body ? body() : null}
      {isNil(fieldForm) ? (
        <div className="workshop-quick__footer">
          <button
            type="button"
            className="workshop-quick__primary"
            disabled={saving || !changed}
            onClick={apply}
          >
            {labels.APPLY}
          </button>
          <button
            type="button"
            className="workshop-quick__secondary"
            disabled={saving}
            onClick={onCancel}
          >
            {labels.CANCEL}
          </button>
        </div>
      ) : null}
    </div>
  );
};

WorkshopQuickEdit.propTypes = {
  row: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  fieldLabels: PropTypes.object.isRequired,
  checkLabels: PropTypes.object.isRequired,
  registry: PropTypes.array,
  known: PropTypes.object,
  actions: PropTypes.array,
  switches: PropTypes.array,
  variables: PropTypes.array,
  checkFor: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onCreateField: PropTypes.func.isRequired,
  onEditField: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onSaveCheck: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

WorkshopQuickEdit.defaultProps = {
  registry: [],
  known: null,
  actions: [],
  switches: [],
  variables: [],
  saving: false,
};

export default WorkshopQuickEdit;
